import React, { useEffect, useState, useRef } from 'react';

const SessionSummary = ({ 
  logData, 
  projectInfo,
  selectedSession,
  sessionInfo: sessionInfoFromHook,
  visibleFiles, 
  setVisibleFiles, 
  audioRef, 
  isPlaying, 
  currentVideoTime, 
  videoRefs,
  recreateRoute,
  unitId,
  selectedProject
}) => {
  const [transcodeStatus, setTranscodeStatus] = React.useState({});
  

  
  // Reset transcode status when session changes
  useEffect(() => {
    setTranscodeStatus({});
  }, [selectedSession]);
  
  // Check transcode status on mount for all video files
  useEffect(() => {
    const checkStatus = async () => {
      const sessionInfo = projectInfo?.sessions?.find(s => s.objectId === selectedSession);
      const files = sessionInfo?.logFiles || [];
      const videoFiles = files.filter(f => ['mkv', 'avi'].includes(f.suffix));
      
      if (videoFiles.length === 0) return;
      
      for (const file of videoFiles) {
        try {
          const url = `${process.env.REACT_APP_BASE_URL}/sessions/transcode-status/${unitId}/${selectedProject}/${selectedSession}/${file.filename}`;
          const response = await fetch(url, {
            credentials: 'include'
          });
          const data = await response.json();
          setTranscodeStatus(prev => ({ ...prev, [file.filename]: data }));
        } catch (error) {
          console.error('Error checking transcode status:', error);
        }
      }
    };
    
    if (selectedSession && unitId && selectedProject && projectInfo) {
      checkStatus();
    }
  }, [selectedSession, unitId, selectedProject, projectInfo]);
  
  const handleCancelTranscode = async (filename) => {
    try {
      await fetch(`${process.env.REACT_APP_BASE_URL}/sessions/transcode/${unitId}/${selectedProject}/${selectedSession}/${filename}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setTranscodeStatus(prev => ({ ...prev, [filename]: { status: 'not_started', progress: 0 } }));
    } catch (error) {
      console.error('Cancel transcode error:', error);
    }
  };
  
  const handleTranscode = async (filename) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/sessions/transcode/${unitId}/${selectedProject}/${selectedSession}/${filename}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      setTranscodeStatus(prev => ({ ...prev, [filename]: data }));
      
      // Poll for status if processing
      if (data.status === 'processing') {
        const pollStatus = setInterval(async () => {
          const statusResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/sessions/transcode-status/${unitId}/${selectedProject}/${selectedSession}/${filename}`, {
            credentials: 'include'
          });
          const statusData = await statusResponse.json();
          
          setTranscodeStatus(prev => {
            const prevStatus = prev[filename]?.status;
            const newStatus = { ...prev, [filename]: statusData };
            
            // If transcode just completed, reload videos
            if (prevStatus === 'processing' && statusData.status === 'complete') {
              console.log('Transcode complete, reloading videos for:', filename);
              setTimeout(() => {
                videoRefs.current.forEach((ref, idx) => {
                  if (ref) {
                    const source = ref.querySelector('source');
                    console.log(`Video ${idx} source:`, source?.src);
                    if (source && source.src.includes(filename.replace(/\.(mkv|avi)$/, ''))) {
                      console.log(`Reloading video ${idx}`);
                      const currentTime = ref.currentTime;
                      const wasPaused = ref.paused;
                      const url = new URL(source.src);
                      url.searchParams.set('_t', Date.now());
                      source.src = url.toString();
                      console.log('New source URL:', source.src);
                      ref.load();
                      ref.addEventListener('loadedmetadata', () => {
                        ref.currentTime = currentTime;
                        if (!wasPaused) ref.play();
                      }, { once: true });
                    }
                  }
                });
              }, 100);
            }
            
            return newStatus;
          });
          
          if (statusData.status !== 'processing') {
            clearInterval(pollStatus);
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Transcode error:', error);
    }
  };
  // Use session info from projectInfo
  const sessionInfo = projectInfo?.sessions?.find(s => 
    s.objectId === selectedSession
  );

  // Use session info from projectInfo for files
  const files = sessionInfo?.logFiles || [];
  
  // Initialize visibleFiles with all files if it's empty and we have files
  useEffect(() => {
    if (files.length > 0 && visibleFiles.length === 0) {
      const allFilenames = files.map(f => f.filename);
      setVisibleFiles(allFilenames);
    }
  }, [files, visibleFiles, setVisibleFiles]);
  
  if (!sessionInfo) return null;
  
  // Calculate duration from session timing
  let duration = null;
  if (Number.isFinite(sessionInfo?.endTimeMicros) && Number.isFinite(sessionInfo?.startTimeMicros)) {
    duration = `${((sessionInfo.endTimeMicros - sessionInfo.startTimeMicros) / 1000000).toFixed(1)}s`;
  }

  return (
    <div className="session-summary" style={{
      background: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      padding: '12px',
      marginBottom: '16px',
      fontSize: '12px'
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
        <div>
          <h6 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold' }}>Session Info</h6>
          <div><strong>Session Folder:</strong> {sessionInfo?.directory || '/'}</div>
          <div><strong>Start Time:</strong> {sessionInfo?.startTime || 'N/A'}</div>
          <div><strong>Duration:</strong> {duration || 'N/A'}</div>
          {sessionInfo?.startTimeMicros !== undefined && (
            <div><strong>Start Offset:</strong> {(sessionInfo.startTimeMicros / 1000000).toFixed(3)}s</div>
          )}
        </div>
        <div>
          <h6 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold' }}>Files ({files.length})</h6>
          <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
            {files.sort((a, b) => {
              const getOrder = (file) => {
                const suffix = file.suffix || file.sourceType;
                if (['mkv', 'avi'].includes(suffix) || file.sourceType === 'video-stream') return 1;
                if (suffix === 'wav' || file.sourceType === 'sound-device') return 2;
                if (suffix === 'gps' || file.sourceType === 'gps-device') return 3;
                if (suffix === 'clg' || file.sourceType === 'can-device') return 4;
                return 5;
              };
              return getOrder(a) - getOrder(b);
            }).map((file, idx) => {
              const filename = file.filename;
              const displayName = file.sourceText || filename;
              const fileType = file.suffix?.toUpperCase() || 'UNKNOWN';
              const isVideo = ['mkv', 'avi'].includes(file.suffix);
              const status = transcodeStatus[filename];
              const needsTranscode = status?.needsTranscode === true;
              
              return (
                <div key={filename} style={{ marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={visibleFiles.includes(filename)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setVisibleFiles(prev => [...prev, filename]);
                      } else {
                        setVisibleFiles(prev => prev.filter(f => f !== filename));
                      }
                    }}
                    style={{ margin: 0 }}
                  />
                  <span style={{ flex: 'none' }}>
                    <strong>{fileType}:</strong> {displayName}
                    {file.syncStartDelay > 0 && (
                      <span style={{ color: '#666', fontSize: '11px' }}> (+{(file.syncStartDelay / 1000000).toFixed(3)}s)</span>
                    )}
                  </span>
                  {isVideo && needsTranscode && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                      {status?.status === 'processing' ? (
                        <>
                          <div style={{ 
                            width: '10px', 
                            height: '10px', 
                            border: '2px solid #f3f3f3', 
                            borderTop: '2px solid #3498db', 
                            borderRadius: '50%', 
                            animation: 'spin 0.6s linear infinite' 
                          }} />
                          <span style={{ fontSize: '10px', color: '#666' }}>Transcoding</span>
                          <div style={{ 
                            width: '40px', 
                            height: '4px', 
                            background: '#e0e0e0', 
                            borderRadius: '2px', 
                            overflow: 'hidden' 
                          }}>
                            <div style={{ 
                              width: `${status.progress}%`, 
                              height: '100%', 
                              background: '#3498db', 
                              transition: 'width 0.3s' 
                            }} />
                          </div>
                          <span style={{ fontSize: '10px', color: '#666' }}>{status.progress}%</span>
                          <button
                            onClick={() => handleCancelTranscode(filename)}
                            style={{
                              padding: '0',
                              fontSize: '12px',
                              width: '14px',
                              height: '14px',
                              lineHeight: '12px',
                              cursor: 'pointer',
                              border: '1px solid #dc3545',
                              borderRadius: '3px',
                              background: '#fff',
                              color: '#dc3545',
                              fontWeight: 'bold'
                            }}
                            title="Cancel transcode"
                          >
                            ×
                          </button>
                        </>
                      ) : status?.status === 'complete' ? (
                        <span style={{ fontSize: '10px', color: '#28a745', fontWeight: 'bold' }}>Transcoded</span>
                      ) : (
                        <button
                          onClick={() => handleTranscode(filename)}
                          style={{
                            padding: '1px 6px',
                            fontSize: '10px',
                            height: '16px',
                            lineHeight: '14px',
                            cursor: 'pointer',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            background: '#fff'
                          }}
                          title="Transcode to MP4"
                        >
                          Transcode
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      

    </div>
  );
};

export default SessionSummary;